import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  createGrmCustomer,
  editGrmCustomer,
  searchGrmProducts,
  createGrmOrder,
  getGrmOrderDetails,
  addGrmOrderNote,
} from "../../../api/grm";
import MemberDetails from "./MemberDetails";
import AddressDetails from "./AddressDetails";
import OrderDetailsForm from "./OrderDetailsForm";
import PrescriptionOpportunity from "./PrescriptionOpportunity";
import CommonDialog from "../../common/CommonDialog";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import { stripTrailingChars } from "../../../utils/formatUtils";

function CustomStepIcon(props) {
  const { active, completed } = props;

  let borderStyle;
  if (completed) {
    borderStyle = "none";
  } else if (active) {
    borderStyle = "3.5px solid #403C6F";
  } else {
    borderStyle = "2px solid #C4C2D3";
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        boxSizing: "border-box",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: completed ? "#403C6F" : "#F7F8FB",
        border: borderStyle,
        zIndex: 1,
      }}
    >
      {completed && (
        <CheckIcon
          sx={{
            color: "#FFFFFF",
            fontSize: "10px",
            stroke: "#FFFFFF",
            strokeWidth: 1,
          }}
        />
      )}
    </Box>
  );
}

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

const steps = ["Customer Info", "Order Details", "Prescription"];

const toLabelContactMethod = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "mobilephone" || normalized === "sms")
    return "Mobile Phone";
  if (normalized === "email") return "Email";
  if (normalized === "alternatephone" || normalized === "homephone")
    return "Alternate Phone";
  if (normalized === "alternateemail") return "Alternate Email";
  return "";
};

const getAvailableContactMethods = (info) => {
  const available = [];
  if ((info.mobileNumber || "").trim()) {
    available.push("Mobile Phone");
  }
  if ((info.alternatePhone || "").trim()) {
    available.push("Alternate Phone");
  }
  return available;
};

const determinePreferredMethod = (info, availableMethods, currentPreferred) => {
  const priorityOrder = ["Mobile Phone", "Alternate Phone"];
  if (!currentPreferred || !availableMethods.includes(currentPreferred)) {
    for (const method of priorityOrder) {
      if (availableMethods.includes(method)) {
        return method;
      }
    }
    return availableMethods[0] || "Mobile Phone";
  }
  return currentPreferred;
};

const splitToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const cleanPhoneNumber = (phone) => {
  if (!phone) return "";
  const originalPhone = String(phone).trim();

  // If it starts with '+', assume international and keep the '+' while removing other non-digits
  if (originalPhone.startsWith("+")) {
    return "+" + originalPhone.substring(1).replace(/\D/g, "");
  } else {
    // Otherwise, assume North American and remove all non-digits
    return originalPhone.replace(/\D/g, "");
  }
};

const normalizeSex = (value) => {
  if (!value) return "";
  const lowerValue = String(value).toLowerCase();
  if (lowerValue.includes("female")) return "Female";
  if (lowerValue.includes("male")) return "Male";
  return "";
};

const boolToYesNo = (value, defaultValue = "") => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return defaultValue;
};

const yesNoToBool = (value, defaultValue = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "yes";
  return defaultValue;
};

const toGrmPreferredCommunication = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "mobile phone") return "MobilePhone";
  if (normalized === "email") return "Email";
  if (normalized === "alternate phone") return "HomePhone";
  if (normalized === "alternate email") return "Email";
  return "MobilePhone";
};

const toGrmAutoRefillCommunication = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "mobile phone") return "Sms";
  if (normalized === "email") return "Email";
  if (normalized === "alternate phone") return "HomePhone";
  if (normalized === "alternate email") return "Email";
  return "Sms";
};

const toCsv = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  return value || "";
};

const getNameParts = (member = {}) => {
  const fullName = (member.fullName || member.name || "").trim();
  const split = fullName ? fullName.split(" ").filter(Boolean) : [];
  return {
    firstName: member.firstName || split[0] || "Some",
    lastName: member.lastName || split.slice(1).join(" ") || "",
  };
};

const convertToIsoDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [m, d, y] = trimmed.split("/");
    return `${y}-${m}-${d}`;
  }
  return null;
};

const convertToMmDdYyyy = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr instanceof Date && !Number.isNaN(dateStr.getTime())) {
    const m = String(dateStr.getMonth() + 1).padStart(2, "0");
    const d = String(dateStr.getDate()).padStart(2, "0");
    const y = dateStr.getFullYear();
    return `${m}/${d}/${y}`;
  }
  const str = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    return str;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const [m, d, y] = str.split("/");
    return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const datePart = str.split("T")[0].split(" ")[0];
    const [y, m, d] = datePart.split("-");
    if (y && m && d) {
      return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
    }
  }
  const dates = str.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b|\b\d{4}-\d{2}-\d{2}\b/g);
  if (dates && dates.length > 0) {
    const lastDate = dates.at(-1);
    return convertToMmDdYyyy(lastDate);
  }
  const parsed = Date.parse(str);
  if (!Number.isNaN(parsed)) {
    const dt = new Date(parsed);
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    const y = dt.getFullYear();
    return `${m}/${d}/${y}`;
  }
  return "";
};

const formatDateToYyyyMmDd = (dateStr) => {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [m, d, y] = trimmed.split("/");
    return `${y}-${m}-${d}`;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [m, d, y] = trimmed.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return trimmed;
};

const buildCreateGrmPayload = (formData, opportunity) => {
  const member = opportunity?.member || {};
  const existing = opportunity?.grmDetails || {};
  const existingCustomer = existing.customerDetails || {};
  const { firstName, lastName } = getNameParts(member);

  const comments = extractComments(opportunity?.grmOrderDetails);
  const parsedComment = parseGrmCommentText(comments);

  const billing = formData?.address?.billing || {};
  const delivery = formData?.address?.delivery || {};
  const medical = formData?.orderDetails?.medical || {};
  const general = formData?.address?.general || {};

  const heightInCm = Number.parseInt(String(medical.height || ""), 10);
  const weightInKg = Number.parseInt(String(medical.weight || ""), 10);

  const payload = {
    firstName,
    lastName,
    billingAddress: {
      line1: billing.street || "",
      line2: billing.line || "",
      city: billing.city || "",
      state: billing.state || "",
      zipCode: billing.zip || "",
    },
    deliveryAddress: {
      line1: delivery.street || "",
      line2: delivery.line || "",
      city: delivery.city || "",
      state: delivery.state || "",
      zipCode: delivery.zip || "",
    },
    division: existingCustomer.division || "",
    insuranceMemberIdentifier: String(
      opportunity?.subscriberNum ||
        opportunity?.member?.subscriberNum ||
        existingCustomer.insuranceMemberIdentifier ||
        member.insuranceMemberIdentifier ||
        "",
    ).slice(0, 20),
    insuranceRelationshipCode:
      member.relCode ||
      opportunity?.relCode ||
      existingCustomer.insuranceRelationshipCode ||
      member.insuranceRelationshipCode ||
      "",
    subscriberId:
      member.personCode || opportunity?.personCode
        ? `${member.subscriberNum || opportunity?.subscriberNum || existingCustomer.subscriberId || ""}-${member.personCode || opportunity?.personCode || ""}`
        : member.subscriberNum ||
          opportunity?.subscriberNum ||
          existingCustomer.subscriberId ||
          "",
    dateOfBirth:
      member.dateOfBirth || member.dob || existingCustomer.dateOfBirth || "",
    heightInCm: Number.isNaN(heightInCm)
      ? existingCustomer.heightInCm || null
      : heightInCm,
    weightInKg: Number.isNaN(weightInKg)
      ? existingCustomer.weightInKg || null
      : weightInKg,
    sex: normalizeSex(medical.sex || existingCustomer.sex) || "",
    isSmoker: yesNoToBool(medical.smoker, existingCustomer.isSmoker || false),
    drugAllergies:
      toCsv(medical.allergies) || toCsv(existingCustomer.drugAllergies) || "",
    currentMedicalConditions:
      toCsv(medical.conditions) ||
      existingCustomer.currentMedicalConditions ||
      "",
    currentMedicationsAndSupplements:
      toCsv(medical.medications) ||
      existingCustomer.currentMedicationsAndSupplements ||
      "",
    email: formData?.address?.general?.email || existing.email || "",
    alternateEmail:
      formData?.address?.general?.alternateEmail ||
      parsedComment.customer?.alternateEmail ||
      existing.alternateEmail ||
      "",
    alternateEmailAddress:
      formData?.address?.general?.alternateEmail ||
      parsedComment.customer?.alternateEmail ||
      existing.alternateEmailAddress ||
      "",
    homePhone: cleanPhoneNumber(
      general.alternatePhone || existing.homePhone || "",
    ),
    mobilePhone: cleanPhoneNumber(
      general.mobileNumber || existing.mobilePhone || member.phoneNumber || "",
    ),
    preferredCommunicationMethod: toGrmPreferredCommunication(
      general.contactMethod || existing.preferredCommunicationMethod,
    ),
    preferredCommunicationMethodForAutoRefills: toGrmAutoRefillCommunication(
      general.refillContactMethod ||
        existing.preferredCommunicationMethodForAutoRefills,
    ),
    sendOrderPlacementConfirmationsEmails:
      existing.sendOrderPlacementConfirmationsEmails || false,
    sendOrderShipmentConfirmationsEmails:
      existing.sendOrderShipmentConfirmationsEmails || false,
    subscribedToFormularyUpdateEmails:
      existing.subscribedToFormularyUpdateEmails || false,
    groupName:
      opportunity?.member?.groupName ||
      opportunity?.groupName ||
      existingCustomer.groupName ||
      "",
    groupCode:
      opportunity?.member?.groupNum ||
      opportunity?.member?.groupId ||
      opportunity?.groupNum ||
      opportunity?.member?.groupCode ||
      opportunity?.groupCode ||
      existingCustomer.groupCode ||
      "",
    carrierCode:
      opportunity?.member?.carrierCode ||
      opportunity?.carrierCode ||
      opportunity?.member?.carrier ||
      opportunity?.carrier ||
      existingCustomer.carrierCode ||
      "",
  };

  const isMale = payload.sex === "Male";
  if (!isMale) {
    payload.isPregnant = yesNoToBool(
      medical.pregnant,
      existingCustomer.isPregnant || false,
    );
    payload.pregnancyDueDate =
      convertToIsoDate(medical.pregnancyDueDate) ||
      convertToIsoDate(existingCustomer.pregnancyDueDate) ||
      null;
    payload.isBreastfeeding = yesNoToBool(
      medical.breastFeeding,
      existingCustomer.isBreastfeeding || false,
    );
  }

  return payload;
};

const isAddressValid = (addr) => {
  return !!(addr && (addr.line1 || addr.street || addr.city));
};

const extractComments = (grmOrderDetails) => {
  if (!grmOrderDetails) return [];
  if (Array.isArray(grmOrderDetails)) return grmOrderDetails;
  if (Array.isArray(grmOrderDetails.comments)) return grmOrderDetails.comments;
  if (Array.isArray(grmOrderDetails.notes)) return grmOrderDetails.notes;
  if (Array.isArray(grmOrderDetails.result?.comments))
    return grmOrderDetails.result.comments;
  if (Array.isArray(grmOrderDetails.result?.notes))
    return grmOrderDetails.result.notes;
  if (Array.isArray(grmOrderDetails.result)) return grmOrderDetails.result;
  if (Array.isArray(grmOrderDetails.data)) return grmOrderDetails.data;
  if (
    typeof grmOrderDetails === "object" &&
    (grmOrderDetails.text || grmOrderDetails.comment || grmOrderDetails.note)
  ) {
    return [grmOrderDetails];
  }
  return [];
};

const cleanVal = (val) => {
  if (!val) return "";
  const trimmed = String(val).trim();
  if (/^(?:is\s+)?(?:unavailable|unavailbale)[.,;]*$/i.test(trimmed)) {
    return "";
  }
  return trimmed;
};

const cleanFaxVal = (val) => {
  if (!val) return "";
  let trimmed = String(val).trim();

  trimmed = trimmed
    .replace(/^(?:Physician\s+fax|fax\s+number|fax)\s+/i, "")
    .trim();

  const changedMatch = /changed\s+(?:from\s+.{0,500}?to\s+|to\s+)(.+)/i.exec(
    trimmed,
  );
  if (changedMatch) {
    trimmed = changedMatch[1].trim();
  }

  trimmed = trimmed.replace(/^["']|["']$/g, "").trim();
  trimmed = trimmed.replace(/^is\s+/i, "").trim();
  trimmed = stripTrailingChars(trimmed, ".,;").trim();

  if (/^(?:is\s+)?(?:unavailable|unavailbale)[.,;]*$/i.test(trimmed)) {
    return "";
  }
  return trimmed;
};

const COMMENT_TEXT_KEYS = ["text", "comment", "note", "content"];

const findTextKey = (item, keys) =>
  item && keys.find((k) => typeof item[k] === "string");

const collectCommentTexts = (commentsInput) => {
  const textItems = [];
  if (Array.isArray(commentsInput)) {
    commentsInput.forEach((item) => {
      if (typeof item === "string") {
        textItems.push(item);
        return;
      }
      const key = findTextKey(item, COMMENT_TEXT_KEYS);
      if (key) textItems.push(item[key]);
    });
  } else if (typeof commentsInput === "string") {
    textItems.push(commentsInput);
  } else if (commentsInput && typeof commentsInput === "object") {
    const key = findTextKey(commentsInput, COMMENT_TEXT_KEYS.slice(0, 3));
    if (key) textItems.push(commentsInput[key]);
  }
  return textItems;
};

const applyPhysicianPairs = (parsedData, parsedPairs) => {
  parsedData.physician = {
    physicianFirstName:
      cleanVal(parsedPairs.firstName || parsedPairs.physicianFirstName) ||
      parsedData.physician.physicianFirstName ||
      "",
    physicianLastName:
      cleanVal(parsedPairs.lastName || parsedPairs.physicianLastName) ||
      parsedData.physician.physicianLastName ||
      "",
    npi: cleanVal(parsedPairs.npi) || parsedData.physician.npi || "",
    dea: cleanVal(parsedPairs.dea) || parsedData.physician.dea || "",
    practiceClinic:
      cleanVal(parsedPairs.practiceClinic) ||
      parsedData.physician.practiceClinic ||
      "",
    phone:
      cleanVal(parsedPairs.phone || parsedPairs.physicianPhone) ||
      parsedData.physician.phone ||
      "",
    fax:
      cleanFaxVal(parsedPairs.fax || parsedPairs.physicianFax) ||
      cleanFaxVal(parsedData.physician.fax) ||
      "",
  };
};

const applyOpportunityPairs = (parsedData, parsedPairs) => {
  parsedData.opportunity = {
    newMedication:
      cleanVal(parsedPairs.newMedication) ||
      parsedData.opportunity.newMedication ||
      "Yes",
    paEffDate:
      cleanVal(parsedPairs.paEffDate) ||
      parsedData.opportunity.paEffDate ||
      "01/01/2026",
    paTermDate:
      cleanVal(parsedPairs.paTermDate) ||
      parsedData.opportunity.paTermDate ||
      "01/01/2026",
    productName:
      cleanVal(parsedPairs.productName) ||
      parsedData.opportunity.productName ||
      "",
    strength:
      cleanVal(parsedPairs.strength) || parsedData.opportunity.strength || "",
    qty: cleanVal(parsedPairs.qty) || parsedData.opportunity.qty || "",
  };
};

const applyAcknowledgementPairs = (parsedData, parsedPairs) => {
  parsedData.acknowledgements = {
    attested:
      typeof parsedPairs.attested === "string"
        ? parsedPairs.attested === "Yes"
        : parsedData.acknowledgements.attested,
    advocateName:
      cleanVal(parsedPairs.advocateName) ||
      parsedData.acknowledgements.advocateName ||
      "",
    advocateEmail:
      cleanVal(parsedPairs.advocateEmail) ||
      parsedData.acknowledgements.advocateEmail ||
      "",
    advocatePhone:
      cleanVal(parsedPairs.advocatePhone) ||
      parsedData.acknowledgements.advocatePhone ||
      "",
  };
};

const applyPreferencePairs = (parsedData, parsedPairs) => {
  parsedData.preferences = {
    quantity:
      cleanVal(parsedPairs.quantity) || parsedData.preferences.quantity || "",
    daysSupply:
      cleanVal(parsedPairs.daysSupply) ||
      parsedData.preferences.daysSupply ||
      "",
  };
};

const applyCustomerPairs = (parsedData, parsedPairs) => {
  parsedData.customer = {
    alternateEmail:
      cleanVal(parsedPairs.alternateEmail) ||
      parsedData.customer.alternateEmail ||
      "",
    groupId:
      cleanVal(parsedPairs.groupId || parsedPairs.insurancePlanId) ||
      parsedData.customer.groupId ||
      "",
    insurancePlanId:
      cleanVal(parsedPairs.insurancePlanId) ||
      parsedData.customer.insurancePlanId ||
      "",
    relationshipCode:
      cleanVal(parsedPairs.relationshipCode) ||
      parsedData.customer.relationshipCode ||
      "",
    groupName:
      cleanVal(parsedPairs.groupName) || parsedData.customer.groupName || "",
  };
};

const PAIR_SECTION_HANDLERS = {
  prescribingPhysician: applyPhysicianPairs,
  orderDetailsReadOnly: applyOpportunityPairs,
  acknowledgements: applyAcknowledgementPairs,
  orderDetails: applyPreferencePairs,
  customerDetails: applyCustomerPairs,
};

const parseKeyValueSection = (section, parsedData) => {
  const colonIdx = section.indexOf(":");
  if (colonIdx === -1) return false;

  const sectionName = section.substring(0, colonIdx).trim();
  const sectionContent = section.substring(colonIdx + 1).trim();

  const pairs = sectionContent.split(", ");
  const parsedPairs = {};
  let hasPairs = false;
  for (const pair of pairs) {
    const eqIdx = pair.indexOf("=");
    if (eqIdx === -1) continue;
    hasPairs = true;
    const key = pair.substring(0, eqIdx).trim();
    const val = pair.substring(eqIdx + 1).trim();
    parsedPairs[key] = cleanVal(val);
  }

  if (!hasPairs) return false;

  if (Object.hasOwn(PAIR_SECTION_HANDLERS, sectionName)) {
    PAIR_SECTION_HANDLERS[sectionName](parsedData, parsedPairs);
  }
  return true;
};

const parsePhysicianProse = (section, parsedData) => {
  const docMatch = section.match(/Dr\.\s+([A-Za-z]+)(?:\s+([A-Za-z]+))?/);
  const npiMatch =
    section.match(/NPI number\s+([a-z0-9]+)/i) ||
    section.match(/NPI\s+([a-z0-9]+)/i);
  const clinicMatch =
    section.match(/practicing at\s+([^,.]+)/i) ||
    section.match(/practice clinic\s+([^,.]+)/i);
  const deaMatch = section.match(/DEA(?: number)?\s+([a-z0-9]+)/i);
  const phoneMatch =
    section.match(/Physician phone number\s+(?:is\s+)?([^,.]+)/i) ||
    section.match(/phone(?: number)?\s+(?:is\s+)?([^,.]+)/i);
  const faxMatch =
    section.match(/Physician fax\s+(?:is\s+)?([^,.]+)/i) ||
    section.match(/fax(?: number)?\s+(?:is\s+)?([^,.]+)/i);

  parsedData.physician = {
    physicianFirstName:
      cleanVal(docMatch?.[1]) || parsedData.physician.physicianFirstName || "",
    physicianLastName:
      cleanVal(docMatch?.[2]) || parsedData.physician.physicianLastName || "",
    npi: cleanVal(npiMatch?.[1]) || parsedData.physician.npi || "",
    dea: cleanVal(deaMatch?.[1]) || parsedData.physician.dea || "",
    practiceClinic:
      cleanVal(clinicMatch?.[1]) || parsedData.physician.practiceClinic || "",
    phone: cleanVal(phoneMatch?.[1]) || parsedData.physician.phone || "",
    fax:
      cleanFaxVal(faxMatch?.[1]) || cleanFaxVal(parsedData.physician.fax) || "",
  };
};

const parseOpportunityProse = (section, parsedData) => {
  const effMatch = section.match(/PA effective date\s+(?:is\s+)?([^.]+)/i);
  const termMatch = section.match(/PA term date\s+(?:is\s+)?([^.]+)/i);
  const medMatch = section.match(
    /indicated that\s(.{0,300}?)\sis a new medication/i,
  );

  parsedData.opportunity = {
    newMedication: medMatch
      ? "Yes"
      : parsedData.opportunity.newMedication || "Yes",
    paEffDate:
      convertToMmDdYyyy(effMatch?.[1]) ||
      parsedData.opportunity.paEffDate ||
      "01/01/2026",
    paTermDate:
      convertToMmDdYyyy(termMatch?.[1]) ||
      parsedData.opportunity.paTermDate ||
      "01/01/2026",
    productName:
      cleanVal(medMatch?.[1]) || parsedData.opportunity.productName || "",
    strength: parsedData.opportunity.strength || "",
    qty: parsedData.opportunity.qty || "",
  };
};

const parseAcknowledgementProse = (section, parsedData) => {
  const advNameMatch = section.match(
    /Patient Advocate name\s+(?:is\s+)?([^.]+)/i,
  );
  const advEmailMatch =
    section.match(
      /Patient Advocate (?:mail|email) address\s+(?:is\s+)?([^\s,]+@[^\s,]+)/i,
    ) ||
    section.match(
      /Patient Advocate (?:mail|email) address\s+(?:is\s+)?([^\s,.][^,.]*?)(?:\.(?=\s*(?:Patient Advocate|Attested|$))|,|$)/i,
    );
  const advPhoneMatch = section.match(
    /Patient Advocate phone number\s+(?:is\s+)?([^.]+)/i,
  );
  const attestedMatch = section.match(/Attested:\s*(Yes|No)/i);

  parsedData.acknowledgements = {
    attested: attestedMatch
      ? attestedMatch[1] === "Yes"
      : parsedData.acknowledgements.attested,
    advocateName:
      cleanVal(advNameMatch?.[1]) ||
      parsedData.acknowledgements.advocateName ||
      "",
    advocateEmail:
      cleanVal(stripTrailingChars(advEmailMatch?.[1] ?? "", ".,;")) ||
      parsedData.acknowledgements.advocateEmail ||
      "",
    advocatePhone:
      cleanVal(advPhoneMatch?.[1]) ||
      parsedData.acknowledgements.advocatePhone ||
      "",
  };
};

const parsePreferenceProse = (section, parsedData) => {
  const qtyMatch = section.match(/Quantity\s+(?:is\s+)?([^,.]+)/i);
  const daysMatch = section.match(/Days supply\s+(?:is\s+)?([^,.]+)/i);

  parsedData.preferences = {
    quantity: cleanVal(qtyMatch?.[1]) || parsedData.preferences.quantity || "",
    daysSupply:
      cleanVal(daysMatch?.[1]) || parsedData.preferences.daysSupply || "",
  };
};

const parseCustomerProse = (section, parsedData) => {
  const groupNameMatch = section.match(/Group Name\s+(?:is\s+)?([^,.]+)/i);
  const groupMatch = section.match(/Group (?:Name|ID)\s+(?:is\s+)?([^,.]+)/i);
  const planMatch = section.match(/Insurance Plan ID\s+(?:is\s+)?([^,.]+)/i);
  const relMatch = section.match(/Relationship code\s+(?:is\s+)?([^,.]+)/i);
  const emailMatch =
    section.match(/Alternate email\s+(?:is\s+)?([^\s,]+@[^\s,]+)/i) ||
    section.match(
      /Alternate email\s+(?:is\s+)?([^\s,.][^,.]*?)(?:\.(?=\s|$)|,|$)/i,
    );

  parsedData.customer = {
    alternateEmail:
      cleanVal(stripTrailingChars(emailMatch?.[1] ?? "", ".,;")) ||
      parsedData.customer.alternateEmail ||
      "",
    groupId:
      cleanVal(planMatch?.[1]) ||
      cleanVal(groupMatch?.[1]) ||
      parsedData.customer.groupId ||
      "",
    insurancePlanId:
      cleanVal(planMatch?.[1]) ||
      cleanVal(groupMatch?.[1]) ||
      parsedData.customer.insurancePlanId ||
      "",
    relationshipCode:
      cleanVal(relMatch?.[1]) || parsedData.customer.relationshipCode || "",
    groupName:
      cleanVal(groupNameMatch?.[1]) || parsedData.customer.groupName || "",
  };
};

const parseProseSection = (section, parsedData) => {
  if (/prescribing physician|Physician Name/i.test(section)) {
    parsePhysicianProse(section, parsedData);
  }
  if (/PA effective date|new medication/i.test(section)) {
    parseOpportunityProse(section, parsedData);
  }
  if (/Patient Advocate|Attested/i.test(section)) {
    parseAcknowledgementProse(section, parsedData);
  }
  if (/Order details:/i.test(section)) {
    parsePreferenceProse(section, parsedData);
  }
  if (/Customer details:/i.test(section)) {
    parseCustomerProse(section, parsedData);
  }
};

const setPhysicianName = (parsedData, newVal) => {
  if (!newVal) {
    parsedData.physician.physicianFirstName = "";
    parsedData.physician.physicianLastName = "";
    return;
  }
  const cleanDr = newVal.replace(/^Dr\.\s*/i, "").trim();
  const parts = cleanDr.split(/\s+/);
  parsedData.physician.physicianFirstName = parts[0] || "";
  parsedData.physician.physicianLastName = parts.slice(1).join(" ") || "";
};

const FIELD_VALUE_RULES = [
  [
    /Patient Advocate Name|Advocate Name/i,
    (p, v) => {
      p.acknowledgements.advocateName = v;
    },
  ],
  [
    /Patient Advocate (?:Email|mail address)|Advocate Email/i,
    (p, v) => {
      p.acknowledgements.advocateEmail = v;
    },
  ],
  [
    /Patient Advocate Phone|Advocate Phone/i,
    (p, v) => {
      p.acknowledgements.advocatePhone = v;
    },
  ],
  [
    /Physician First Name/i,
    (p, v) => {
      p.physician.physicianFirstName = v;
    },
  ],
  [
    /Physician Last Name/i,
    (p, v) => {
      p.physician.physicianLastName = v;
    },
  ],
  [
    /Physician Name|Doctor Name|prescribing physician name|^\s*name\s*$/i,
    (p, v) => setPhysicianName(p, v),
  ],
  [
    /NPI/i,
    (p, v) => {
      p.physician.npi = v;
    },
  ],
  [
    /DEA/i,
    (p, v) => {
      p.physician.dea = v;
    },
  ],
  [
    /Practice Clinic|Practice \/ Clinic|Clinic/i,
    (p, v) => {
      p.physician.practiceClinic = v;
    },
  ],
  [
    /Physician Phone|phone number|phone/i,
    (p, v) => {
      p.physician.phone = v;
    },
  ],
  [
    /Physician Fax|fax number|fax/i,
    (p, v) => {
      p.physician.fax = cleanFaxVal(v);
    },
  ],
  [
    /PA Effective Date|PA eff date|Effective Date/i,
    (p, v) => {
      p.opportunity.paEffDate = v;
    },
  ],
  [
    /PA Term Date|PA term date|Term Date/i,
    (p, v) => {
      p.opportunity.paTermDate = v;
    },
  ],
  [
    /New Medication/i,
    (p, v) => {
      p.opportunity.newMedication = v;
    },
  ],
  [
    /Attested/i,
    (p, v) => {
      p.acknowledgements.attested = v.toLowerCase() === "yes";
    },
  ],
  [
    /Quantity/i,
    (p, v) => {
      p.preferences.quantity = v;
      p.opportunity.qty = v;
    },
  ],
  [
    /Days Supply/i,
    (p, v) => {
      p.preferences.daysSupply = v;
    },
  ],
  [
    /Alternate Email|Alternate mail/i,
    (p, v) => {
      p.customer.alternateEmail = v;
    },
  ],
  [
    /Group Name/i,
    (p, v) => {
      p.customer.groupName = v;
    },
  ],
  [
    /Insurance Plan ID|Group ID|Group Number/i,
    (p, v) => {
      p.customer.insurancePlanId = v;
      p.customer.groupId = v;
    },
  ],
  [
    /Relationship Code/i,
    (p, v) => {
      p.customer.relationshipCode = v;
    },
  ],
];

const applyFieldValue = (parsedData, fieldName, value) => {
  const rule = FIELD_VALUE_RULES.find(([pattern]) => pattern.test(fieldName));
  if (rule) rule[1](parsedData, value);
};

const normalizeFieldName = (rawFieldName) =>
  rawFieldName
    .replace(/^[.,;:\s]+/, "")
    .replace(
      /^(?:The prescribing physician is|Order details:|Customer details:)\s*/i,
      "",
    )
    .trim();

const FIELD_NAME_CHAR = /[a-z0-9\s/]/i;
const MAX_FIELD_NAME_LENGTH = 100;
const CHANGE_HEADER_REGEX = /\schanged from(?=\s)/gi;
const TO_SEPARATOR_REGEX = /["']?\s+to\s+/iy;

const isQuote = (char) => char === '"' || char === "'";

const indexOfQuote = (text, from) => {
  let index = from;
  while (index < text.length && !isQuote(text[index])) {
    index += 1;
  }
  return index;
};

// Walks back from the "changed from" position to recover the field name.
const findFieldNameStart = (section, changeIndex, minStart) => {
  const limit = Math.max(minStart, changeIndex - MAX_FIELD_NAME_LENGTH);
  let start = changeIndex;
  while (start > limit && FIELD_NAME_CHAR.test(section[start - 1])) {
    start -= 1;
  }
  return start;
};

// Reads `<old> to <new>` after the "changed from" header, using the last
// "to" separator that precedes the first quote after the old value.
const readChangedValues = (section, headerEnd) => {
  let valueStart = headerEnd;
  while (valueStart < section.length && /\s/.test(section[valueStart])) {
    valueStart += 1;
  }
  const hasLeadingQuote = isQuote(section[valueStart]);
  const oldStart = hasLeadingQuote ? valueStart + 1 : valueStart;
  const oldLimit = indexOfQuote(section, oldStart);
  // Without a leading quote, the separator may reuse trailing whitespace.
  const minSeparatorStart = hasLeadingQuote ? oldStart : headerEnd + 1;

  for (let k = oldLimit; k >= minSeparatorStart; k -= 1) {
    TO_SEPARATOR_REGEX.lastIndex = k;
    if (TO_SEPARATOR_REGEX.test(section)) {
      const separatorEnd = TO_SEPARATOR_REGEX.lastIndex;
      const newStart = isQuote(section[separatorEnd])
        ? separatorEnd + 1
        : separatorEnd;
      const newEnd = indexOfQuote(section, newStart);
      return {
        newValue: section.slice(newStart, newEnd),
        end: isQuote(section[newEnd]) ? newEnd + 1 : newEnd,
      };
    }
  }
  return null;
};

const parseChangedValueNotes = (section, parsedData) => {
  CHANGE_HEADER_REGEX.lastIndex = 0;
  let headerMatch;
  let previousEnd = 0;
  while ((headerMatch = CHANGE_HEADER_REGEX.exec(section)) !== null) {
    const changed = readChangedValues(section, CHANGE_HEADER_REGEX.lastIndex);
    if (!changed) {
      continue;
    }

    const fieldStart = findFieldNameStart(
      section,
      headerMatch.index,
      previousEnd,
    );
    previousEnd = changed.end;
    CHANGE_HEADER_REGEX.lastIndex = changed.end;
    if (fieldStart === headerMatch.index) {
      continue;
    }

    const fieldName = normalizeFieldName(
      section.slice(fieldStart, headerMatch.index),
    );
    const rawNewVal = changed.newValue.trim();
    const newVal = /^(?:is\s+)?(?:unavailable|unavailbale)[.,;]*$/i.test(
      rawNewVal,
    )
      ? ""
      : rawNewVal;

    applyFieldValue(parsedData, fieldName, newVal);
  }
};

const addGrmOrderNotesInOrder = async (orderId, sectionTexts, index = 0) => {
  if (index >= sectionTexts.length) return;
  const cleanSecText = (sectionTexts[index] || "")
    .replaceAll('"', "")
    .replaceAll("\\", "");
  const noteResult = await addGrmOrderNote(orderId, { text: cleanSecText });
  if (!noteResult || noteResult.success === false) {
    throw new Error(
      noteResult?.error ||
        noteResult?.message ||
        "Failed to add note to order in GRM",
    );
  }
  await addGrmOrderNotesInOrder(orderId, sectionTexts, index + 1);
};

const parseUnavailableNotes = (section, parsedData) => {
  const unavailRegex = /([a-z0-9\s/]{1,100}?)\s(?:unavailable|unavailbale)/gi;
  let unavailMatch;
  while ((unavailMatch = unavailRegex.exec(section)) !== null) {
    const fieldName = normalizeFieldName(unavailMatch[1] || "");

    if (/changed\s+(?:from|to)/i.test(fieldName)) {
      continue;
    }

    applyFieldValue(parsedData, fieldName, "");
  }
};

const parseGrmCommentText = (commentsInput) => {
  const parsedData = {
    physician: {},
    opportunity: {},
    acknowledgements: {},
    preferences: {},
    customer: {},
  };

  if (!commentsInput) {
    return parsedData;
  }

  const textItems = collectCommentTexts(commentsInput);

  for (const itemText of textItems) {
    const sections = itemText.split(" | ");
    for (const section of sections) {
      if (parseKeyValueSection(section, parsedData)) {
        continue;
      }

      parseProseSection(section, parsedData);
      parseChangedValueNotes(section, parsedData);
      parseUnavailableNotes(section, parsedData);
    }
  }

  return parsedData;
};

const formatAttestedText = (value) => {
  if (value === undefined || value === null || value === "") {
    return "unavailable";
  }
  return value ? "Yes" : "No";
};

const resolveContactMethod = (method, initialInfo, available) => {
  if (!method || !available.includes(method)) {
    return determinePreferredMethod(initialInfo, available, method);
  }
  return method;
};

const getInitialFormData = (opportunity) => {
  const member = opportunity?.member || {};
  const grmDetails = opportunity?.grmDetails || {};
  const customerDetails = grmDetails?.customerDetails || {};

  const comments = extractComments(opportunity?.grmOrderDetails);
  const parsedComment = parseGrmCommentText(comments);

  const memberOrderDetails = member.orderDetails || member.order || {};
  const memberPhysician = member.physician || member.prescribingPhysician || {};
  const memberAcknowledgements =
    member.acknowledgements || member.acknowledgment || member.advocate || {};
  const attested = false;
  const billingAddress =
    (isAddressValid(opportunity?.billingAddress) &&
      opportunity.billingAddress) ||
    (isAddressValid(customerDetails?.billingAddress) &&
      customerDetails.billingAddress) ||
    (isAddressValid(member?.address) && member.address) ||
    {};
  const deliveryAddress =
    (isAddressValid(opportunity?.deliveryAddress) &&
      opportunity.deliveryAddress) ||
    (isAddressValid(customerDetails?.deliveryAddress) &&
      customerDetails.deliveryAddress) ||
    (isAddressValid(member?.deliveryAddress) && member.deliveryAddress) ||
    {};

  const conditions = splitToArray(
    customerDetails.currentMedicalConditions || member.currentMedicalConditions,
  );
  const medications = splitToArray(
    customerDetails.currentMedicationsAndSupplements ||
      member.currentMedicationsAndSupplements,
  );

  const initialMobile =
    grmDetails.mobilePhone ||
    member.mobilePhone ||
    member.phoneNumber ||
    member.phone ||
    "";
  const initialAltPhone =
    grmDetails.homePhone ||
    member.alternatePhone ||
    member.alternatePhoneNum ||
    member.altPhone ||
    "";
  const initialEmail =
    grmDetails.email || member.email || member.contact?.email || "";
  const initialAltEmailRaw = String(
    parsedComment.customer?.alternateEmail ||
      customerDetails.alternateEmail ||
      customerDetails.alternateEmailAddress ||
      grmDetails.alternateEmail ||
      grmDetails.alternateEmailAddress ||
      opportunity?.alternateEmail ||
      opportunity?.alternateEmailAddress ||
      member.alternateEmail ||
      member.alternateEmailAddress ||
      member.altEmail ||
      "",
  ).trim();
  const initialAltEmail = stripTrailingChars(initialAltEmailRaw, ".,;: \t\r\n");

  const initialInfo = {
    mobileNumber: initialMobile,
    alternatePhone: initialAltPhone,
    email: initialEmail,
    alternateEmail: initialAltEmail,
  };

  const available = getAvailableContactMethods(initialInfo);

  const contactMethod = resolveContactMethod(
    opportunity?.preferredCommunicationMethod ||
      toLabelContactMethod(grmDetails?.preferredCommunicationMethod),
    initialInfo,
    available,
  );

  const refillContactMethod = resolveContactMethod(
    opportunity?.preferredCommunicationMethodForAutoRefills ||
      toLabelContactMethod(
        grmDetails?.preferredCommunicationMethodForAutoRefills,
      ),
    initialInfo,
    available,
  );

  return {
    address: {
      general: {
        mobileNumber: initialMobile,
        alternatePhone: initialAltPhone,
        email: initialEmail,
        alternateEmail: initialAltEmail,
        contactMethod,
        refillContactMethod,
      },
      billing: {
        street: billingAddress.line1 || billingAddress.street || "",
        line: billingAddress.line2 || billingAddress.line || "",
        city: billingAddress.city || "",
        state: billingAddress.state || "",
        zip:
          billingAddress.zipCode ||
          billingAddress.postalCode ||
          billingAddress.zip ||
          "",
      },
      delivery: {
        street: deliveryAddress.line1 || deliveryAddress.street || "",
        line: deliveryAddress.line2 || deliveryAddress.line || "",
        city: deliveryAddress.city || "",
        state: deliveryAddress.state || "",
        zip:
          deliveryAddress.zipCode ||
          deliveryAddress.postalCode ||
          deliveryAddress.zip ||
          "",
      },
      copyBilling: false,
    },
    orderDetails: {
      medical: {
        height: customerDetails.heightInCm
          ? `${customerDetails.heightInCm}cm`
          : "",
        weight: customerDetails.weightInKg
          ? `${customerDetails.weightInKg}kg`
          : "",
        pregnant: boolToYesNo(customerDetails.isPregnant, "No"),
        breastFeeding: boolToYesNo(customerDetails.isBreastfeeding, "No"),
        smoker: boolToYesNo(customerDetails.isSmoker, "No"),
        conditions,
        medications,
        allergies:
          customerDetails.drugAllergies ||
          member.drugAllergies ||
          member.allergies ||
          member.allergy ||
          "",
        sex: capitalizeFirstLetter(
          customerDetails.sex || member.sex || member.gender || "",
        ),
        pregnancyDueDate: convertToMmDdYyyy(
          customerDetails.pregnancyDueDate || "",
        ),
      },
      opportunity: {
        newMedication:
          parsedComment.opportunity?.newMedication ||
          memberOrderDetails.newMedication ||
          member.newMedication ||
          opportunity?.newMedication ||
          "Yes",
        paEffDate:
          convertToMmDdYyyy(
            parsedComment.opportunity?.paEffDate ||
              memberOrderDetails.paEffDate ||
              member.paEffDate ||
              opportunity?.paEffDate,
          ) || "01/01/2026",
        paTermDate:
          convertToMmDdYyyy(
            parsedComment.opportunity?.paTermDate ||
              memberOrderDetails.paTermDate ||
              member.paTermDate ||
              opportunity?.paTermDate,
          ) || "01/01/2026",
        quantity:
          parsedComment.opportunity?.qty ||
          memberOrderDetails.quantity ||
          member.quantity ||
          opportunity?.metricQty ||
          "",
        daysSupply:
          memberOrderDetails.daysSupply ||
          member.daysSupply ||
          opportunity?.daySupply ||
          "",
      },
      physician: {
        physicianFirstName:
          parsedComment.physician?.physicianFirstName ||
          memberPhysician.physicianFirstName ||
          memberPhysician.firstName ||
          member.physicianFirstName ||
          opportunity?.physicianFirstName ||
          "",
        physicianLastName:
          parsedComment.physician?.physicianLastName ||
          memberPhysician.physicianLastName ||
          memberPhysician.lastName ||
          member.physicianLastName ||
          opportunity?.physicianLastName ||
          "",
        npi:
          parsedComment.physician?.npi ||
          memberPhysician.npi ||
          member.npi ||
          opportunity?.npi ||
          "",
        dea:
          parsedComment.physician?.dea ||
          memberPhysician.dea ||
          member.dea ||
          opportunity?.dea ||
          "",
        practiceClinic:
          parsedComment.physician?.practiceClinic ||
          memberPhysician.practiceClinic ||
          memberPhysician.clinic ||
          member.practiceClinic ||
          opportunity?.practiceClinic ||
          opportunity?.pharmacy ||
          "",
        phone:
          parsedComment.physician?.phone ||
          parsedComment.physician?.physicianPhone ||
          memberPhysician.phone ||
          memberPhysician.phoneNumber ||
          memberPhysician.physicianPhone ||
          opportunity?.providerDetails?.phone ||
          opportunity?.providerDetails?.phoneNumber ||
          opportunity?.provider?.phone ||
          opportunity?.provider?.contact ||
          opportunity?.phone ||
          opportunity?.physicianPhone ||
          "",
        fax: cleanFaxVal(
          parsedComment.physician?.fax ||
            parsedComment.physician?.physicianFax ||
            memberPhysician.fax ||
            memberPhysician.faxNumber ||
            memberPhysician.physicianFax ||
            opportunity?.providerDetails?.fax ||
            opportunity?.providerDetails?.faxNumber ||
            opportunity?.provider?.fax ||
            opportunity?.fax ||
            opportunity?.physicianFax ||
            "",
        ),
      },
    },
    prescription: {
      documents: {
        prescriptionRx: "Optional",
        proofOfResidency: "Optional",
        driversLicence: "Optional",
        insuranceCard: "Optional",
        priorAuth: "Optional",
      },
      acknowledgements: {
        attested,
        advocateName:
          parsedComment.acknowledgements?.advocateName ||
          memberAcknowledgements.advocateName ||
          (opportunity?.assignee?.name &&
          opportunity.assignee.name !== "Unassigned"
            ? opportunity.assignee.name
            : "") ||
          (typeof opportunity?.assignee === "string" &&
          opportunity.assignee !== "Unassigned"
            ? opportunity.assignee
            : "") ||
          (member.assignee && member.assignee !== "Unassigned"
            ? member.assignee
            : "") ||
          opportunity?.patientCareAdvocate ||
          "",
        advocateEmail:
          parsedComment.acknowledgements?.advocateEmail ||
          memberAcknowledgements.advocateEmail ||
          member.assigneeEmail ||
          opportunity?.assigneeEmail ||
          "",
        advocatePhone:
          parsedComment.acknowledgements?.advocatePhone ||
          memberAcknowledgements.advocatePhone ||
          member.assigneePhone ||
          member.assigneePhoneNumber ||
          opportunity?.assigneePhone ||
          "",
      },
      preferences: {
        quantity:
          parsedComment.preferences?.quantity || opportunity?.metricQty || "",
        daysSupply:
          parsedComment.preferences?.daysSupply || opportunity?.daySupply || "",
      },
    },
  };
};

const buildCommentSectionsMap = (
  physician,
  opportunityDetails,
  acknowledgements,
  preferences,
  general,
  localOpportunity,
  initialFormData = null,
) => {
  const sectionMap = {
    physician: "",
    paDates: "",
    acknowledgements: "",
    preferences: "",
    customer: "",
  };

  const formatName = (str) => {
    if (!str) return "";
    if (str === str.toUpperCase()) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
    return str;
  };

  const cleanVal = (val) => {
    if (val === undefined || val === null) return "";
    const s = String(val).trim();
    if (/^unavailable$/i.test(s) || /^unavailbale$/i.test(s)) return "";
    return s;
  };

  const initialPhysician = initialFormData?.orderDetails?.physician || {};
  const initialOpportunityDetails =
    initialFormData?.orderDetails?.opportunity || {};
  const initialAcknowledgements =
    initialFormData?.prescription?.acknowledgements || {};
  const initialPreferences = initialFormData?.prescription?.preferences || {};
  const initialGeneral = initialFormData?.address?.general || {};

  // 1. Prescribing Physician
  const pFirst = formatName(physician.physicianFirstName?.trim());
  const pLast = formatName(physician.physicianLastName?.trim());
  const npi = physician.npi?.trim();
  const practiceClinic = physician.practiceClinic?.trim();
  const dea = physician.dea?.trim();
  const phone = physician.phone?.trim() || physician.physicianPhone?.trim();
  const fax = physician.fax?.trim() || physician.physicianFax?.trim();

  const initPFirst = formatName(initialPhysician.physicianFirstName?.trim());
  const initPLast = formatName(initialPhysician.physicianLastName?.trim());
  const initNpi = initialPhysician.npi?.trim();
  const initPracticeClinic = initialPhysician.practiceClinic?.trim();
  const initDea = initialPhysician.dea?.trim();
  const initPhone =
    initialPhysician.phone?.trim() || initialPhysician.physicianPhone?.trim();
  const initFax =
    initialPhysician.fax?.trim() || initialPhysician.physicianFax?.trim();

  let physicianStr = "The prescribing physician is ";
  const currDrName = [pFirst, pLast].filter(Boolean).join(" ");
  const initDrName = [initPFirst, initPLast].filter(Boolean).join(" ");

  if (initialFormData && cleanVal(currDrName) !== cleanVal(initDrName)) {
    physicianStr += `name changed from ${initDrName ? "Dr. " + initDrName : "unavailable"} to ${currDrName ? "Dr. " + currDrName : "unavailable"}`;
  } else if (pFirst || pLast) {
    physicianStr += `Dr. ${currDrName}`;
  } else {
    physicianStr += "the attending physician";
  }

  const getNpiText = () => {
    if (initialFormData && cleanVal(npi) !== cleanVal(initNpi)) {
      return `NPI number changed from ${cleanVal(initNpi) || "unavailable"} to ${cleanVal(npi) || "unavailable"}`;
    }
    return npi ? `with NPI number ${npi}` : "NPI number unavailable";
  };

  const getClinicText = () => {
    if (
      initialFormData &&
      cleanVal(practiceClinic) !== cleanVal(initPracticeClinic)
    ) {
      return `Practice clinic changed from ${cleanVal(initPracticeClinic) || "unavailable"} to ${cleanVal(practiceClinic) || "unavailable"}`;
    }
    return practiceClinic
      ? `practicing at ${practiceClinic}`
      : "practice clinic unavailable";
  };

  const getDeaText = () => {
    if (initialFormData && cleanVal(dea) !== cleanVal(initDea)) {
      return `DEA number changed from ${cleanVal(initDea) || "unavailable"} to ${cleanVal(dea) || "unavailable"}`;
    }
    return dea ? `DEA number ${dea}` : "DEA number unavailable";
  };

  const getPhoneText = () => {
    if (initialFormData && cleanVal(phone) !== cleanVal(initPhone)) {
      return `phone number changed from ${cleanVal(initPhone) || "unavailable"} to ${cleanVal(phone) || "unavailable"}`;
    }
    return phone ? `phone number ${phone}` : "phone number unavailable";
  };

  const getFaxText = () => {
    if (initialFormData && cleanVal(fax) !== cleanVal(initFax)) {
      return `fax number changed from ${cleanVal(initFax) || "unavailable"} to ${cleanVal(fax) || "unavailable"}`;
    }
    return fax ? `fax number ${fax}` : "fax number unavailable";
  };

  const details = [
    getNpiText(),
    getClinicText(),
    getDeaText(),
    getPhoneText(),
    getFaxText(),
  ];
  physicianStr += `, ${details.join(", ")}.`;
  sectionMap.physician = physicianStr;

  // 2. Order Details / PA Dates
  const paEff = formatDateToYyyyMmDd(opportunityDetails.paEffDate);
  const paTerm = formatDateToYyyyMmDd(opportunityDetails.paTermDate);

  const initPaEff = formatDateToYyyyMmDd(initialOpportunityDetails.paEffDate);
  const initPaTerm = formatDateToYyyyMmDd(initialOpportunityDetails.paTermDate);

  const getPaEffText = () => {
    if (initialFormData && cleanVal(paEff) !== cleanVal(initPaEff)) {
      return `PA effective date changed from ${cleanVal(initPaEff) || "unavailable"} to ${cleanVal(paEff) || "unavailable"}`;
    }
    return paEff
      ? `PA effective date is ${paEff}`
      : "PA effective date unavailable";
  };

  const getPaTermText = () => {
    if (initialFormData && cleanVal(paTerm) !== cleanVal(initPaTerm)) {
      return `PA term date changed from ${cleanVal(initPaTerm) || "unavailable"} to ${cleanVal(paTerm) || "unavailable"}`;
    }
    return paTerm ? `PA term date is ${paTerm}` : "PA term date unavailable";
  };

  const paParts = [getPaEffText(), getPaTermText()];
  sectionMap.paDates = `${paParts.join(". ")}.`;

  // 3. Acknowledgements / Patient Advocate
  const advName = acknowledgements.advocateName?.trim();
  const advEmail = acknowledgements.advocateEmail?.trim();
  const advPhone = acknowledgements.advocatePhone?.trim();
  const attested = acknowledgements.attested;

  const initAdvName = initialAcknowledgements.advocateName?.trim();
  const initAdvEmail = initialAcknowledgements.advocateEmail?.trim();
  const initAdvPhone = initialAcknowledgements.advocatePhone?.trim();
  const initAttested = initialAcknowledgements.attested;

  const getAdvNameText = () => {
    if (initialFormData && cleanVal(advName) !== cleanVal(initAdvName)) {
      return `Patient Advocate name changed from ${cleanVal(initAdvName) || "unavailable"} to ${cleanVal(advName) || "unavailable"}`;
    }
    return advName
      ? `Patient Advocate name is ${advName}`
      : "Patient Advocate name unavailable";
  };

  const getAdvEmailText = () => {
    if (initialFormData && cleanVal(advEmail) !== cleanVal(initAdvEmail)) {
      return `Patient Advocate mail address changed from ${cleanVal(initAdvEmail) || "unavailable"} to ${cleanVal(advEmail) || "unavailable"}`;
    }
    return advEmail
      ? `Patient Advocate mail address is ${advEmail}`
      : "Patient Advocate mail address unavailable";
  };

  const getAdvPhoneText = () => {
    if (initialFormData && cleanVal(advPhone) !== cleanVal(initAdvPhone)) {
      return `Patient Advocate phone number changed from ${cleanVal(initAdvPhone) || "unavailable"} to ${cleanVal(advPhone) || "unavailable"}`;
    }
    return advPhone
      ? `Patient Advocate phone number is ${advPhone}`
      : "Patient Advocate phone number unavailable";
  };

  const getAttestedText = () => {
    const attestedStr = formatAttestedText(attested);
    const initAttestedStr = formatAttestedText(initAttested);
    if (initialFormData && attestedStr !== initAttestedStr) {
      return `Attested changed from ${initAttestedStr} to ${attestedStr}`;
    }
    return `Attested: ${attestedStr}`;
  };

  const advParts = [
    getAdvNameText(),
    getAdvEmailText(),
    getAdvPhoneText(),
    getAttestedText(),
  ];
  sectionMap.acknowledgements = `${advParts.join(". ")}.`;

  // 4. Order Preferences (Quantity, Days Supply)
  const quantity = preferences.quantity ?? localOpportunity?.metricQty;
  const daysSupply = preferences.daysSupply ?? localOpportunity?.daySupply;

  const initQuantity =
    initialPreferences.quantity ?? localOpportunity?.metricQty;
  const initDaysSupply =
    initialPreferences.daysSupply ?? localOpportunity?.daySupply;

  const getQuantityText = () => {
    if (initialFormData && cleanVal(quantity) !== cleanVal(initQuantity)) {
      return `Quantity changed from ${cleanVal(initQuantity) || "unavailable"} to ${cleanVal(quantity) || "unavailable"}`;
    }
    return quantity ? `Quantity is ${quantity}` : "Quantity unavailable";
  };

  const getDaysSupplyText = () => {
    if (initialFormData && cleanVal(daysSupply) !== cleanVal(initDaysSupply)) {
      return `Days supply changed from ${cleanVal(initDaysSupply) || "unavailable"} to ${cleanVal(daysSupply) || "unavailable"}`;
    }
    return daysSupply
      ? `Days supply is ${daysSupply}`
      : "Days supply unavailable";
  };

  const ordParts = [getQuantityText(), getDaysSupplyText()];
  sectionMap.preferences = `Order details: ${ordParts.join(", ")}.`;

  // 5. Customer Details
  const groupName =
    localOpportunity?.member?.groupName || localOpportunity?.groupName || "";
  const insurancePlanId =
    localOpportunity?.member?.groupNum ||
    localOpportunity?.member?.groupId ||
    localOpportunity?.groupNum ||
    "";
  const relCode =
    localOpportunity?.member?.relCode || localOpportunity?.relCode || "";
  const altEmail = general?.alternateEmail?.trim();

  const initAltEmail = initialGeneral?.alternateEmail?.trim();

  const getGroupNameText = () => {
    return groupName ? `Group Name is ${groupName}` : "Group Name unavailable";
  };

  const getPlanIdText = () => {
    return insurancePlanId
      ? `Insurance Plan ID is ${insurancePlanId}`
      : "Insurance Plan ID unavailable";
  };

  const getRelCodeText = () => {
    return relCode
      ? `Relationship code is ${relCode}`
      : "Relationship code unavailable";
  };

  const getAltEmailText = () => {
    if (initialFormData && cleanVal(altEmail) !== cleanVal(initAltEmail)) {
      return `Alternate email changed from ${cleanVal(initAltEmail) || "unavailable"} to ${cleanVal(altEmail) || "unavailable"}`;
    }
    return altEmail
      ? `Alternate email is ${altEmail}`
      : "Alternate email unavailable";
  };

  const custParts = [
    getGroupNameText(),
    getPlanIdText(),
    getRelCodeText(),
    getAltEmailText(),
  ];
  sectionMap.customer = `Customer details: ${custParts.join(", ")}.`;

  Object.keys(sectionMap).forEach((key) => {
    if (sectionMap[key]) {
      sectionMap[key] = sectionMap[key]
        .replaceAll('"', "")
        .replaceAll("\\", "");
    }
  });

  return sectionMap;
};

const InternationalEnrollmentModal = ({
  open,
  onClose,
  opportunity,
  onSuccess,
}) => {
  const localOpportunity = React.useMemo(() => {
    const comments = extractComments(opportunity?.grmOrderDetails);
    if (!comments || (Array.isArray(comments) && comments.length === 0)) {
      return opportunity;
    }
    const parsedComment = parseGrmCommentText(comments);
    return {
      ...opportunity,
      drug: parsedComment.opportunity?.productName || opportunity?.drug,
      strength: parsedComment.opportunity?.strength || opportunity?.strength,
      metricQty:
        parsedComment.opportunity?.qty ||
        parsedComment.preferences?.quantity ||
        opportunity?.metricQty,
      daySupply:
        parsedComment.preferences?.daysSupply || opportunity?.daySupply,
    };
  }, [opportunity]);

  const [activeStep, setActiveStep] = useState(0);
  const [customerId, setCustomerId] = useState(
    () => localOpportunity?.grmDetails?.id || null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState(null);
  const [stepsValidation, setStepsValidation] = useState({
    0: false, // Customer Info
    1: false, // Order Details
    2: false, // Prescription
  });
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successType, setSuccessType] = useState(""); // "submit" or "update"

  const showError = useCallback((msg, showModal = true) => {
    setSubmissionError(msg);
    if (showModal) {
      setErrorModalOpen(true);
    }
  }, []);

  const contentRef = useRef(null);

  // Clear submission error when activeStep changes
  useEffect(() => {
    setSubmissionError("");
  }, [activeStep]);

  // Reset to first step and clear errors when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setShowErrors(false);
      setErrorModalOpen(false);
      setSuccessModalOpen(false);
      setSuccessType("");
      setSubmissionError("");
    }
  }, [open]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  // Consolidated form data to persist across steps
  const [formData, setFormData] = useState(() =>
    getInitialFormData(localOpportunity),
  );
  const [initialFormDataState, setInitialFormDataState] = useState(() =>
    getInitialFormData(localOpportunity),
  );
  const [initialCommentSectionsMap, setInitialCommentSectionsMap] = useState(
    {},
  );

  useEffect(() => {
    if (open) {
      const initialData = getInitialFormData(localOpportunity);
      setFormData(initialData);
      setInitialFormDataState(initialData);
      setCustomerId(localOpportunity?.grmDetails?.id || null);
      setLastSubmittedPayload(null);
      setStepsValidation({
        0: false,
        1: false,
        2: false,
      });

      const initSectionsMap = buildCommentSectionsMap(
        initialData.orderDetails?.physician || {},
        initialData.orderDetails?.opportunity || {},
        initialData.prescription?.acknowledgements || {},
        initialData.prescription?.preferences || {},
        initialData.address?.general || {},
        localOpportunity,
      );
      setInitialCommentSectionsMap(initSectionsMap);
    }
  }, [open, localOpportunity]);

  const handleDataChange = useCallback((stepKey, sectionKey, newData) => {
    setFormData((prev) => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        [sectionKey]: newData,
      },
    }));
  }, []);

  const handleAddressDataChange = useCallback(
    (section, data) => {
      handleDataChange("address", section, data);
    },
    [handleDataChange],
  );

  const handleOrderDetailsDataChange = useCallback(
    (section, data) => {
      handleDataChange("orderDetails", section, data);
    },
    [handleDataChange],
  );

  const handlePrescriptionDataChange = useCallback(
    (section, data) => {
      handleDataChange("prescription", section, data);
    },
    [handleDataChange],
  );

  const setIsStepValid = useCallback(
    (isValid) => {
      setStepsValidation((prev) => {
        if (prev[activeStep] === isValid) return prev;
        return { ...prev, [activeStep]: isValid };
      });
    },
    [activeStep],
  );

  const editExistingCustomer = async (resolvedId, payload) => {
    let hasDetailsChanged = false;
    if (lastSubmittedPayload) {
      hasDetailsChanged =
        JSON.stringify(lastSubmittedPayload) !== JSON.stringify(payload);
    } else {
      const initialFormData = getInitialFormData(localOpportunity);
      const initialPayload = buildCreateGrmPayload(
        initialFormData,
        localOpportunity,
      );
      hasDetailsChanged =
        JSON.stringify(initialPayload) !== JSON.stringify(payload);
    }

    if (!hasDetailsChanged) {
      console.log(
        "No details changed for existing customer. Going to next step without API call.",
      );
      setCustomerId(resolvedId);
      setLastSubmittedPayload(payload);
      setActiveStep((prev) => prev + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Details changed for existing customer. Calling edit API...");
      const editResult = await editGrmCustomer(resolvedId, payload);
      if (!editResult || editResult.success === false) {
        throw new Error(
          editResult?.error ||
            editResult?.message ||
            "Failed to update customer details in GRM",
        );
      }
      console.log("Customer details updated successfully.");
      setCustomerId(resolvedId);
      setLastSubmittedPayload(payload);
      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update customer details:", error);
      showError(error.message || String(error), false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const createNewCustomer = async (payload) => {
    setIsSubmitting(true);
    try {
      console.log("No existing customer found. Calling create API...");
      const createResult = await createGrmCustomer(payload);
      if (!createResult || createResult.success === false) {
        throw new Error(
          createResult?.error ||
            createResult?.message ||
            "Failed to create customer in GRM",
        );
      }
      const resolvedCustomerId =
        createResult.customerAccount?.id ||
        createResult.customerDetails?.id ||
        createResult.id ||
        createResult.result?.customerAccount?.id ||
        createResult.result?.id;

      if (!resolvedCustomerId) {
        throw new Error("Customer created, but no CustomerId was returned");
      }
      console.log("Customer created with ID:", resolvedCustomerId);
      setCustomerId(resolvedCustomerId);
      setLastSubmittedPayload(payload);
      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to create customer:", error);
      showError(error.message || String(error), false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomerSubmit = async () => {
    const existingCustomerId = localOpportunity?.grmDetails?.id || null;
    const resolvedId = customerId || existingCustomerId;
    const payload = buildCreateGrmPayload(formData, localOpportunity);

    if (resolvedId) {
      await editExistingCustomer(resolvedId, payload);
    } else {
      await createNewCustomer(payload);
    }
  };

  const resolveProductOptionId = async () => {
    let productOptionId = localOpportunity?.grmProductOptionId;
    const hasVendorRefId =
      localOpportunity?.vendorReferenceId ||
      localOpportunity?.vendorPreferenceId ||
      localOpportunity?.program?.vendorReferenceId;

    if (productOptionId || hasVendorRefId) {
      return productOptionId;
    }

    const ndcValue =
      localOpportunity.ndc ||
      localOpportunity.drugNdc ||
      localOpportunity.drug_ndc ||
      localOpportunity.ndcCode ||
      "";
    if (!ndcValue) {
      return null;
    }

    const groupCode =
      localOpportunity?.member?.groupNum ||
      localOpportunity?.member?.groupId ||
      localOpportunity?.groupNum ||
      localOpportunity?.groupId ||
      localOpportunity?.member?.groupCode ||
      localOpportunity?.groupCode ||
      "";

    const productSearchPayload = { searchTerm: ndcValue, groupCode };
    const productSearchResult = await searchGrmProducts(productSearchPayload);
    if (productSearchResult?.result?.length > 0) {
      return productSearchResult.result[0].productOptionId;
    }
    return null;
  };

  const fetchAndLogOrderDetails = async (orderId) => {
    if (!orderId) {
      console.warn("Order created but no order ID returned to fetch history.");
      return;
    }
    try {
      console.log("Fetching order details for orderId:", orderId);
      const orderDetails = await getGrmOrderDetails(orderId);
      console.log("Order details (history):", orderDetails);
    } catch (err) {
      console.error("Failed to fetch order details:", err);
    }
  };

  const handleFinalOrderSubmit = async () => {
    setIsSubmitting(true);
    try {
      const vendorReferenceId =
        localOpportunity?.vendorReferenceId ||
        localOpportunity?.vendorPreferenceId ||
        localOpportunity?.program?.vendorReferenceId;

      if (vendorReferenceId) {
        console.log(
          "vendorReferenceId is present (editing GRM). Executing addGrmOrderNote ONLY...",
          vendorReferenceId,
        );
        const cleanOrderId = String(vendorReferenceId)
          .replaceAll("#", "")
          .replaceAll(/GRM/gi, "")
          .trim();

        const physician = formData.orderDetails?.physician || {};
        const opportunityDetails = formData.orderDetails?.opportunity || {};
        const acknowledgements = formData.prescription?.acknowledgements || {};
        const preferences = formData.prescription?.preferences || {};
        const general = formData.address?.general || {};

        const baseInitial =
          initialFormDataState || getInitialFormData(localOpportunity);

        const currentSectionsMap = buildCommentSectionsMap(
          physician,
          opportunityDetails,
          acknowledgements,
          preferences,
          general,
          localOpportunity,
          baseInitial,
        );

        const baseInitialSectionsMap =
          Object.keys(initialCommentSectionsMap).length > 0
            ? initialCommentSectionsMap
            : buildCommentSectionsMap(
                baseInitial.orderDetails?.physician || {},
                baseInitial.orderDetails?.opportunity || {},
                baseInitial.prescription?.acknowledgements || {},
                baseInitial.prescription?.preferences || {},
                baseInitial.address?.general || {},
                localOpportunity,
              );

        const changedSectionTexts = Object.keys(currentSectionsMap)
          .filter((key) => {
            const currentText = currentSectionsMap[key];
            const initialText = baseInitialSectionsMap[key] || "";
            return currentText && currentText !== initialText;
          })
          .map((key) => currentSectionsMap[key]);

        console.log("Changed section notes to send:", changedSectionTexts);

        // Notes must be added in order and stop at the first failure.
        await addGrmOrderNotesInOrder(cleanOrderId, changedSectionTexts);
        console.log("Changed notes added successfully to order");
        setInitialFormDataState(formData);
        const updatedInitialSectionsMap = buildCommentSectionsMap(
          physician,
          opportunityDetails,
          acknowledgements,
          preferences,
          general,
          localOpportunity,
        );
        setInitialCommentSectionsMap(updatedInitialSectionsMap);
        setSuccessType("update");
        setSuccessModalOpen(true);
        return;
      }

      const productOptionId = await resolveProductOptionId();
      if (!productOptionId) {
        throw new Error("Could not find a matching product from search.");
      }

      const memberProgramId =
        localOpportunity?.programId || localOpportunity?.program?.id || "";
      const isNewMedication =
        formData.orderDetails?.opportunity?.newMedication?.toLowerCase() !==
        "no";

      const physician = formData.orderDetails?.physician || {};
      const opportunityDetails = formData.orderDetails?.opportunity || {};
      const acknowledgements = formData.prescription?.acknowledgements || {};
      const preferences = formData.prescription?.preferences || {};
      const general = formData.address?.general || {};

      const currentSectionsMap = buildCommentSectionsMap(
        physician,
        opportunityDetails,
        acknowledgements,
        preferences,
        general,
        localOpportunity,
      );
      const commentSections = Object.values(currentSectionsMap).filter(Boolean);

      const orderPayload = {
        customerAccountId: customerId,
        items: [{ productOptionId, isNewMedication }],
        comments: commentSections.map((sec) => ({ text: sec })),
      };

      console.log(
        "Creating GRM order for memberProgramId:",
        memberProgramId,
        "payload:",
        orderPayload,
      );
      const orderResult = await createGrmOrder(memberProgramId, orderPayload);
      if (!orderResult || orderResult.success === false) {
        throw new Error(
          orderResult?.error ||
            orderResult?.message ||
            "Failed to create order in GRM",
        );
      }

      console.log("Order created successfully:", orderResult);
      const orderId =
        orderResult?.result?.id ||
        orderResult?.result?.[0]?.id ||
        orderResult?.id;
      await fetchAndLogOrderDetails(orderId);

      setSuccessType("submit");
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Enrollment submission failed:", error);
      showError(`Enrollment failed: ${error.message || error}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  const allStepsValid =
    stepsValidation[0] === true &&
    stepsValidation[1] === true &&
    stepsValidation[2] === true;
  const isFinalStep = activeStep === steps.length - 1;
  // Only disable the Submit button (final step) — Next button is always clickable
  const isButtonDisabled = isSubmitting || (isFinalStep && !allStepsValid);

  let buttonText = "Next";
  if (isFinalStep) {
    buttonText = isSubmitting ? "Submitting..." : "Submit";
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "90vw",
              lg: "80vw",
              xl: "75vw",
            },
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
          },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "90vh",
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              color: "#10100F",
            }}
          >
            International Enrollment - Step {activeStep + 1} of {steps.length}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {submissionError && (
              <Typography
                sx={{
                  color: "#E53935",
                  fontSize: "12px",
                  fontWeight: 500,
                  maxWidth: { xs: "250px", sm: "400px", md: "500px" },
                  wordBreak: "break-word",
                }}
              >
                {submissionError}
              </Typography>
            )}
            <IconButton
              onClick={onClose}
              sx={{ color: "#403C6F" }}
              size="small"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Sticky Header Content */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
          <MemberDetails opportunity={localOpportunity} />

          <Box sx={{ width: "95%", mx: "auto" }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": {
                  fontWeight: 500,
                  fontSize: "13px",
                  color: "#A09EBA",
                  marginTop: "6px",
                },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#403C6F",
                  fontWeight: 600,
                },
                "& .MuiStepLabel-iconContainer": {
                  padding: 0,
                },
                "& .MuiStepConnector-root": {
                  top: "6px",
                  left: "calc(-50% + 7px)",
                  right: "calc(50% + 7px)",
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#C4C2D3",
                  borderTopWidth: 2,
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DialogContent
            ref={contentRef}
            sx={{
              p: 0,
              pb: 2,
              backgroundColor: "transparent",
              overflowX: "hidden",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              // custom scrollbar styling to make UI cleaner
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#D8D6E2",
                borderRadius: "4px",
              },
            }}
          >
            {/* Step Content Area */}
            <Box sx={{ flex: 1, minHeight: "350px", pr: 1 }}>
              {activeStep === 0 && (
                <AddressDetails
                  opportunity={localOpportunity}
                  data={formData.address}
                  onDataChange={handleAddressDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                />
              )}
              {activeStep === 1 && (
                <OrderDetailsForm
                  opportunity={localOpportunity}
                  data={formData.orderDetails}
                  onDataChange={handleOrderDetailsDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                />
              )}
              {activeStep === 2 && (
                <PrescriptionOpportunity
                  opportunity={localOpportunity}
                  data={formData.prescription}
                  onDataChange={handlePrescriptionDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                  customerId={customerId}
                />
              )}
            </Box>
          </DialogContent>

          {/* Footer Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              pt: 2,
              pb: 2,
              px: 3,
              mx: -2,
              mb: -2,
              mt: "auto",
              backgroundColor: "#fff",
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
            }}
          >
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={() => setActiveStep((prev) => prev - 1)}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#403C6F",
                  borderColor: "#9F9DB6",
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "rgba(64, 60, 111, 0.04)",
                    borderColor: "#403C6F",
                  },
                }}
              >
                Back
              </Button>
            )}

            <Button
              variant="contained"
              onClick={async () => {
                if (stepsValidation[activeStep]) {
                  setShowErrors(false);
                } else {
                  setShowErrors(true);
                  return;
                }

                if (activeStep === 1) {
                  await handleCustomerSubmit();
                  return;
                }

                if (!isFinalStep) {
                  setActiveStep((prev) => prev + 1);
                  return;
                }

                await handleFinalOrderSubmit();
              }}
              disabled={isButtonDisabled}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&.Mui-disabled": {
                  backgroundColor: "rgba(64, 60, 111, 0.4)",
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "&:hover": {
                  backgroundColor: "#312e5e",
                  boxShadow: "none",
                },
              }}
            >
              {buttonText}
            </Button>
          </Box>
        </LocalizationProvider>
      </Box>

      {/* Error Popup Modal */}
      <CommonDialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        variant="alert"
        icon={<ErrorOutlineIcon sx={{ color: "#E53935", fontSize: "32px" }} />}
        title="Submission Failed"
        actions={
          <Button
            variant="contained"
            onClick={() => setErrorModalOpen(false)}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#403C6F",
              color: "#FFF",
              px: 6,
              py: 1,
              boxShadow: "none",
              width: "100%",
              maxWidth: "200px",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        }
      >
        An unexpected error occurred while submitting to GRM, Please try again.
        If the issue persists, contact your system administrator.
      </CommonDialog>

      {/* Success Popup Modal */}
      <CommonDialog
        open={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          if (onSuccess) onSuccess();
          onClose();
        }}
        variant="alert"
        icon={<CheckIcon sx={{ color: "#137333", fontSize: "32px" }} />}
        iconBgColor="#E6F4EA"
        title={successType === "update" ? "Order Updated" : "Order Submitted"}
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setSuccessModalOpen(false);
              if (onSuccess) onSuccess();
              onClose();
            }}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#403C6F",
              color: "#FFF",
              px: 6,
              py: 1,
              boxShadow: "none",
              width: "100%",
              maxWidth: "200px",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        }
      >
        {successType === "update"
          ? "The updated information has been successfully submitted to GRM for processing."
          : "The enrollment request has been successfully submitted to GRM for processing."}
      </CommonDialog>
    </Dialog>
  );
};

InternationalEnrollmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunity: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default InternationalEnrollmentModal;
